// import components
import { Box, PopperProps, Tooltip, styled } from '@mui/material';
import { dragActionActiveAtom } from 'components/roster-portal/activity-planning/atoms';
import { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import { ZoomLevel as DetailZoomLevel } from 'components/roster-portal/activity-planning/components/Header/ZoomLevelMenu.enums';
// import hooks
import { Component, useMemo, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useRecoilValue } from 'recoil';
import { useEventListener } from 'usehooks-ts';
import { useSelector } from 'hooks';

// import types
import { PlanningEntityIdentifier } from 'generated/index';
import { ZoomLevel } from 'components/roster-portal/activity-planning/TimelineView/TimelineBar/TimelineBar.enums';

// import utils
import { addSeconds, format, isValid } from 'date-fns';
import { BLUE_TERTIARY, PRIMARY } from 'constants/colors';
import { getSecondsPerPixel } from 'components/roster-portal/activity-planning/TimelineView/utils';

const RouteContainer = styled(Box, {
  shouldForwardProp: (p) => !['isOver', 'detailZoomLevel'].includes(p as string)
})<{
  isOver: boolean;
  detailZoomLevel: DetailZoomLevel;
}>(({ isOver, detailZoomLevel }) => ({
  position: 'relative',
  top: '50%',
  width: '100%',
  height: detailZoomLevel === DetailZoomLevel.COMPACT ? '60px' : '75px',
  padding: '4px',
  boxSizing: 'border-box',
  backgroundColor: 'rgba(0, 0, 0, 0.015)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  border: `2px solid ${isOver ? PRIMARY : BLUE_TERTIARY}`
}));

type Props = {
  planningEntity: PlanningEntity;
  children: React.ReactNode;
  start: Date;
  positionOffset: number;
  scheduleStart?: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
};

export default function TimelineTooltip({
  children,
  start,
  positionOffset,
  planningEntity,
  scheduleStart,
  planningEntityIdentifier,
  zoomLevel,
  timeBarItemWidth
}: Props) {
  const [open, setOpen] = useState(false);
  const dragActionActive = useRecoilValue(dragActionActiveAtom);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const defaultDetailZoomLevel = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;

  const popperRef = useRef<Component<PopperProps, any, any>>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0
  });

  const { setNodeRef, isOver } = useDroppable({
    id: planningEntity.id,
    data: {
      position,
      planningEntityId: planningEntity.planningEntityIdentifier?.entityId,
      scheduleStart: scheduleStart,
      planningEntityIdentifier,
      isToPlan: false
    }
  });
  const handleMouseMove = (event: MouseEvent) => {
    if (!dragActionActive) {
      setOpen(false);
      return;
    }
    // Handling tooltip
    positionRef.current = { x: event.clientX, y: event.clientY };

    if (isOver) {
      setOpen(true);
    } else {
      setOpen(false);
    }

    // Handling mouse position for time values
    if (!areaRef.current) return;
    const bounds = areaRef.current.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;

    setPosition({
      x,
      y
    });
  };

  useEventListener('mousemove', handleMouseMove);
  const secondsPerPixel = getSecondsPerPixel(timeBarItemWidth, zoomLevel);

  const tooltipTime = useMemo(() => {
    const newDate = addSeconds(
      start,
      (position.x - positionOffset) * secondsPerPixel
    );

    if (!isValid(newDate)) return;

    const time = format(newDate, 'HH:mm');

    return time;
  }, [start, position.x]);

  return (
    <Tooltip
      placement="top"
      enterDelay={500}
      open={open}
      title={tooltipTime}
      followCursor
      arrow
      slotProps={{
        popper: {
          ref: popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              const { y, height } = areaRef.current!.getBoundingClientRect();

              return new DOMRect(
                positionRef.current.x - positionOffset,
                y + height / 2,
                0,
                0
              );
            }
          },
          style: {
            zIndex: 10,
            left: positionRef.current.x - positionOffset,
            top: positionRef.current.y
          }
        }
      }}
    >
      <Box
        display="flex"
        width="100%"
        ref={setNodeRef}
        sx={{
          userSelect: 'none'
        }}
      >
        <RouteContainer
          ref={areaRef}
          isOver={isOver}
          detailZoomLevel={detailZoomLevel}
        >
          {children}
        </RouteContainer>
      </Box>
    </Tooltip>
  );
}
